import Table from 'cli-table3';
import pc from 'picocolors';
import { CliError, ExitCode } from './errors';

export type OutputFormat = 'table' | 'json';

/**
 * 把 untrusted 字串（從 API 回來的 employer-controlled 內容）變成終端機可安全列印的形式。
 *
 * 防的是 terminal escape injection：
 * - CSI / OSC / DCS 等 ESC 開頭序列（清螢幕、改 title、移動游標、假超連結 phishing 等）
 * - 其他 C0 / C1 控制字元（保留 \t \n \r 三個合法格式化字元）
 *
 * JSON 模式不需要 sanitize：JSON.stringify 會把 < 0x20 的字元 escape 成 \uXXXX。
 * 只有 table / plain-text 印到 stdout/stderr 的字串走這個 helper。
 *
 * 用 new RegExp(string) 建構，所有控制字元以 \\uNNNN 形式撰寫，避免 source 內含 literal 控制字元。
 */
const ANSI_ESCAPE_SEQUENCE = new RegExp(
	[
		// CSI: ESC [ params intermediates final
		'\\u001B\\[[0-?]*[ -/]*[@-~]',
		// OSC: ESC ] payload (any chars except BEL/ESC) terminated by BEL or ESC \
		'\\u001B\\][^\\u0007\\u001B]*(?:\\u0007|\\u001B\\\\)',
		// Two-char escapes: ESC + Fe final byte (0x40-0x5F = @ A B ... Z [ \ ] ^ _).
		// 涵蓋 CSI([) / OSC(]) / DCS(P) / SOS(X) / ST(\\) / PM(^) / APC(_) intro。
		// CSI / OSC regex 在前面 OR-分支會先匹配對應序列；這條兜底所有未覆蓋 Fe。
		'\\u001B[@-_]',
	].join('|'),
	'g'
);

// 單行用：剝所有 C0（含 \t \n \r）、DEL、C1。table cell、label-prefixed 標題、
// error / warning 訊息都走這條 —— 即便 ANSI 已剝乾淨，殘留 \r 仍能把 cursor 拉回
// 行首蓋掉前面的內容；\n 會打斷表格排版、可能偽造後續 row；\t 寬度可變、
// 搞壞 cli-table3 對齊。
const CONTROL_CHARS_STRICT = new RegExp('[\\u0000-\\u001F\\u007F\\u0080-\\u009F]', 'g');

// 多行用：內部先把 \r\n / lone \r 正規化成 \n，再剝其他 C0（含 \t）、DEL、C1。
// 保留 \n 作為合法段落分隔。Caller 不需事先做正規化。
const CONTROL_CHARS_MULTILINE = new RegExp('[\\u0000-\\u0009\\u000B-\\u001F\\u007F\\u0080-\\u009F]', 'g');

export function sanitizeForTerminal(s: string): string {
	return s.replace(ANSI_ESCAPE_SEQUENCE, '').replace(CONTROL_CHARS_STRICT, '');
}

export function sanitizeForTerminalMultiline(s: string): string {
	return s.replace(ANSI_ESCAPE_SEQUENCE, '').replace(/\r\n?/g, '\n').replace(CONTROL_CHARS_MULTILINE, '');
}

export function resolveOutputFormat(explicit: string | undefined): OutputFormat {
	if (explicit === undefined) {
		return process.stdout.isTTY ? 'table' : 'json';
	}
	if (explicit !== 'json' && explicit !== 'table') {
		throw new CliError(`Invalid --output "${explicit}". Allowed: table, json`, ExitCode.InvalidArgument);
	}
	return explicit;
}

export function isColorEnabled(noColor: boolean | undefined): boolean {
	if (noColor === true) return false;
	if (process.env.NO_COLOR) return false;
	return process.stdout.isTTY ?? false;
}

export function printJson(value: unknown): void {
	process.stdout.write(JSON.stringify(value, null, 2) + '\n');
}

/**
 * Emit one newline-delimited JSON record (ND-JSON). JSON.stringify escapes control
 * chars to \uXXXX, so employer-controlled string values are terminal-safe without
 * extra sanitization. Used by `jobs view --batch`, where one record per line keeps a
 * single failure isolated to its own line.
 */
export function printNdjsonLine(value: unknown): void {
	process.stdout.write(JSON.stringify(value) + '\n');
}

export interface TableColumn<T> {
	header: string;
	value: (row: T) => string;
	maxWidth?: number;
}

export function printTable<T>(rows: T[], columns: TableColumn<T>[], color: boolean): void {
	if (rows.length === 0) {
		process.stdout.write(color ? pc.dim('(no results)\n') : '(no results)\n');
		return;
	}
	const table = new Table({
		head: columns.map((c) => (color ? pc.bold(c.header) : c.header)),
		style: { head: [], border: [] },
		colWidths: columns.map((c) => c.maxWidth ?? null),
		wordWrap: true,
	});
	for (const row of rows) {
		// 每個 cell 的字串都過 sanitize：防 employer-controlled 內容（title/company/area/salary 等）注入 escape。
		table.push(columns.map((c) => sanitizeForTerminal(c.value(row))));
	}
	process.stdout.write(table.toString() + '\n');
}

export function printError(message: string, color: boolean): void {
	const prefix = color ? pc.red('Error:') : 'Error:';
	// Server 回的 error message 也視為 untrusted，sanitize。
	process.stderr.write(`${prefix} ${sanitizeForTerminal(message)}\n`);
}

export function printWarn(message: string, color: boolean): void {
	const prefix = color ? pc.yellow('Warning:') : 'Warning:';
	process.stderr.write(`${prefix} ${sanitizeForTerminal(message)}\n`);
}

export function dim(text: string, color: boolean): string {
	return color ? pc.dim(text) : text;
}
